import { validateInvite } from "../api";
import { holdField } from "./holdField";
import { LOGO_M } from "../logo";

// v0.20 — invite-only gate. Shown ONLY on the create path when the gate is on
// (returning users log in and never reach here). Validates the code against the
// worker, then hands the validated code back so the create ceremony can consume
// it. Deep links (mdrop.it/invite/CODE) arrive here with the code pre-filled.
//
// 1.4.37 (Alexander, 17 Oct — the onboarding of betterthan.cash → globalmoneyapp.com):
// - no Continue, no Submit: the code is checked the moment its sixth symbol is in (typed,
//   pasted, or carried by an invite link), the same smooth flow as an M® Note's code;
// - the line "If you have a code, you already know." is in the accent colour;
// - the field is borderless; its placeholder is "C0DE" in the serial's mono font with a
//   slashed zero (the code alphabet itself has no 0 or O, so the slash is the word's alone);
// - a code that passes reads "Access granted", then the name screen follows by itself.
// 1.4.38: a tap takes C0DE away and shows a dimmed outline of the field (home21.css);
// 1.4.41: *Request access* (muted, above Login) turns the field into an email field and a button;
// the email goes to the Worker's list (POST /access/request), then "Thanks — we'll be in touch."

const ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";
const CODE_LEN = 6;
/** How long "Access granted" stands before the name screen. */
const GRANTED_MS = 900;

/** Uppercase, keep only alphabet chars (drops the display hyphen/spaces), cap 6. */
function normalize(raw: string): string {
  return (raw ?? "")
    .toUpperCase()
    .split("")
    .filter((ch) => ALPHABET.includes(ch))
    .join("")
    .slice(0, CODE_LEN);
}

/** "4KT9RM" -> "4KT-9RM" (hyphen only once we're past the split point). */
function formatCode(code: string): string {
  return code.length > 3 ? `${code.slice(0, 3)}-${code.slice(3)}` : code;
}

interface InviteViewDeps {
  /** Code from a deep link, pre-filled into the box (and checked at once). */
  prefillCode?: string;
  /** Called with the validated canonical code — the create ceremony consumes it. */
  onContinue: (code: string) => void;
  /** "Login" — back to the welcome/login screen. */
  onLogin: () => void;
  /** v1.1.26 — an invite link whose code didn't check out: say why at once. */
  initialError?: string;
  /** 1.4.43 — open on Request access (the email field) rather than the code. */
  ask?: boolean; // 1.4.73 — no longer read (no Request access on the code screen)
}

export function renderInviteView(
  root: HTMLElement,
  { prefillCode, onContinue, onLogin, initialError }: InviteViewDeps,
) {
  root.innerHTML = `
    <main class="screen welcome invite lg21 inv37 slot69-screen">
      <div class="brand">${LOGO_M}</div>
      <!-- 1.4.70 (Alexander, 21 Oct) — the code field stands where the landing's pill stands (.slot69), centred on it;
           the line keeps its 28 px above the field; the rest hangs under it. -->
      <div class="slot69">
        <p class="line69 inv37-line above69">If you have a code, you already know.</p>
        <div class="codewrap inv37-wrap">
          <input id="code" class="codebox inv37-code" type="text" inputmode="text"
                 autocapitalize="characters" autocomplete="one-time-code" autocorrect="off"
                 spellcheck="false" maxlength="40" placeholder="C0DE"
                 aria-label="Invite code" />
        </div>
        <div class="below69">
          <p id="status" class="status inv37-status" role="status" aria-live="polite"></p>
        </div>
      </div>
    </main>
  `;

  const input = root.querySelector<HTMLInputElement>("#code")!;
  const statusEl = root.querySelector<HTMLElement>("#status")!;
  holdField(input, () => root.querySelector(".inv37-line")); // 1.4.70 — the fixed-position rule (the take page's)

  let value = normalize(prefillCode ?? "");
  let checking = false;
  let done = false;

  const paint = () => {
    input.value = formatCode(value);
    const end = input.value.length;
    try {
      input.setSelectionRange(end, end);
    } catch {
      /* some mobile browsers reject setSelectionRange on certain input types */
    }
  };

  const say = (text: string, kind: "" | "warn" | "ok" = "") => {
    statusEl.textContent = text;
    statusEl.classList.toggle("warn", kind === "warn");
    statusEl.classList.toggle("ok", kind === "ok");
  };
  const clearError = () => {
    say("");
    input.classList.remove("err");
  };

  input.addEventListener("input", () => {
    if (checking || done) { paint(); return; }
    value = normalize(input.value);
    clearError();
    paint();
    if (value.length === CODE_LEN) void submit();
  });

  // 1.4.72 (Alexander, 21 Oct) — no Login on the code screen (none on the landing since 1.4.69).
  root.querySelector<HTMLButtonElement>("#login")?.addEventListener("click", () => { if (!done) onLogin(); });

  // 1.4.73 (Alexander, 21 Oct) — no Request access on the code screen: it lives on the landing alone.

  async function submit() {
    if (value.length !== CODE_LEN || checking || done) return;
    checking = true;
    input.readOnly = true;
    input.classList.add("busy");
    clearError();
    say("Checking…");
    try {
      const r = await validateInvite(value);
      if (r.valid) {
        done = true;
        input.classList.remove("busy");
        input.classList.add("ok");
        input.blur(); // the keyboard goes: the screen is a moment of its own
        say("Access granted", "ok");
        const code = value;
        window.setTimeout(() => onContinue(code), GRANTED_MS);
        return;
      }
      fail(
        r.reason === "used"
          ? "That code has already been used. Ask your inviter for another."
          : "That code isn't valid. Check it and try again.",
      );
    } catch {
      fail("Couldn't check that code. Try again in a moment.");
    } finally {
      checking = false;
    }
  }

  function fail(message: string) {
    input.readOnly = false;
    input.classList.remove("busy");
    input.classList.add("err");
    say(message, "warn");
  }

  paint();
  if (initialError) {
    say(initialError, "warn");
    if (/code|invite/i.test(initialError)) input.classList.add("err"); // the code was the problem (not a cancelled Face ID)
  } else if (value.length === CODE_LEN) {
    void submit(); // a link's code: checked at once, the same path as one typed
    return;
  }
  // 1.4.38: not focused at load — C0DE stands until the person taps the field, and the tap
  // shows the outline (on a phone, focus without a tap raises no keyboard anyway).
}
